"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { CodingCodebook } from "@/components/tools/qualitative-text/coding-codebook";
import { CodingDocumentPane } from "@/components/tools/qualitative-text/coding-document";
import { CodingExcerpts } from "@/components/tools/qualitative-text/coding-excerpts";
import { CodingExport } from "@/components/tools/qualitative-text/coding-export";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  addCode,
  addDocument,
  addSegment,
  codeCounts,
  coverage,
  emptyProject,
  excerptsFor,
  type Project,
  parseProject,
  removeCode,
  removeDocument,
  removeSegment,
  renameCode,
  serializeProject,
  setMemo,
} from "@/lib/tool-kit/qualitative-text/coding";
import { buildSampleProject } from "@/lib/tool-kit/qualitative-text/coding-sample";

/** English strings. A translated content file overrides these through `ui`. */
export const QUALITATIVE_CODING_TOOL_STRINGS = {
  privacy:
    "Your documents and codes are saved in this browser only (local storage) and never uploaded. Clearing site data deletes them, so download a project backup regularly.",
  saveFailed:
    "This browser would not save your work (storage may be full or blocked). Download a project backup now.",
  documentsHeading: "Documents",
  documentLabel: "Document to code",
  noDocument:
    "Add a transcript, field note or open-ended answer to start coding.",
  addDocumentHeading: "Add a document",
  titleLabel: "Title",
  titlePlaceholder: "Interview 1",
  textLabel: "Text",
  textPlaceholder:
    "Paste the transcript or notes here. The text cannot be edited once added, so passages keep their positions.",
  addDocument: "Add document",
  untitled: "Document {number}",
  trySample: "Try a sample",
  removeDocument: "Remove this document",
  removeDocumentConfirm: "Confirm: remove it and its coded passages",
  readingHeading: "Code the text",
  readingHint:
    "Select words with the mouse or keyboard to code them with the chosen code. Use the + button beside a paragraph to code the whole paragraph.",
  readingLabel: "The document text, with coded passages highlighted",
  codeParagraph: "Code this paragraph with the chosen code",
  noCodeYet:
    "Create a code in the codebook first, then select text to apply it.",
  autoApply: "Apply the chosen code as soon as I select text",
  selectionLabel: "Selected passage",
  applySelection: "Code selection with “{code}”",
  chooseCodeFirst: "Choose a code in the codebook to apply the selection.",
  codebookHeading: "Codebook",
  codebookHint:
    "Choose the code to apply, and nest codes under a parent to build themes. Counts show passages for the code, then in total with its sub-codes.",
  newCodeLabel: "New code",
  newCodePlaceholder: "Cost",
  parentLabel: "Inside",
  noParent: "Top level",
  addCode: "Add code",
  activeCode: "Chosen code",
  rename: "Rename",
  renameLabel: "Code name",
  save: "Save",
  cancel: "Cancel",
  remove: "Remove",
  removeCodeConfirm: "Confirm: remove with sub-codes and passages",
  countFormat: "{direct} ({total})",
  noCodes: "No codes yet.",
  statDocuments: "Documents",
  statCodes: "Codes",
  statPassages: "Coded passages",
  statCoverage: "Text coded",
  percent: "{value}%",
  excerptsHeading: "Excerpts by code",
  filterLabel: "Show passages for",
  allCodes: "All codes",
  includeChildren: "Include sub-codes",
  noExcerpts: "No coded passages to show yet.",
  excerptsTruncated:
    "Showing the first {shown} of {total} passages. The exports include all of them.",
  memoLabel: "Memo",
  removeExcerpt: "Remove",
  location: "{document}, characters {start}–{end}",
  exportHeading: "Export and back up",
  exportHint:
    "The CSV and Markdown exports list every passage under its code. The project file holds everything, so you can restore it on another device.",
  exportCsv: "Excerpts (CSV)",
  exportMarkdown: "Excerpts (Markdown)",
  exportCodebook: "Codebook (CSV)",
  exportProject: "Project backup (JSON)",
  importProject: "Restore a project backup",
  importFailed: "That file is not a CitationEasy coding project.",
  clearAll: "Delete everything",
  clearAllConfirm: "Confirm: delete all documents and codes",
  csvHead: {
    document: "document",
    code: "code",
    excerpt: "excerpt",
    start: "start_character",
    end: "end_character",
    memo: "memo",
  },
  codebookHead: {
    code: "code",
    parent: "parent",
    passages: "passages",
    total: "passages_with_subcodes",
  },
  markdownTitle: "Coded excerpts",
  markdownCharacters: "characters",
  markdownEmpty: "No excerpts yet",
  filenames: {
    csv: "coded-excerpts.csv",
    markdown: "coded-excerpts.md",
    codebook: "codebook.csv",
    project: "coding-project.json",
  },
  sample: {
    title: "Interview 4 (sample)",
    text: "I live forty minutes from campus, so the bus fare is a real problem. Some weeks I skip a lecture because I can't afford the pass. My lecturers have been kind about it, though. One of them lent me her notes and told me to email whenever I was stuck.\nHonestly, the thing that would help most is somewhere cheap to eat. I spend more on lunch than I do on books.\nMy friend Dani helps too. We study together on Thursdays, and that's the only reason I keep going on bad weeks.",
    barriers: "Barriers",
    cost: "Cost",
    support: "Support",
    staff: "Staff",
    peers: "Peers",
    quoteFare: "the bus fare is a real problem",
    quotePass: "Some weeks I skip a lecture because I can't afford the pass.",
    quoteLunch: "I spend more on lunch than I do on books.",
    quoteStaff:
      "One of them lent me her notes and told me to email whenever I was stuck.",
    quotePeers:
      "We study together on Thursdays, and that's the only reason I keep going on bad weeks.",
  },
} as const;

type Strings = typeof QUALITATIVE_CODING_TOOL_STRINGS;

const STORAGE_KEY = "citationeasy.qualitative-coding.v1";
const PERCENT = 100;

function sampleProject(strings: Strings): Project {
  const s = strings.sample;
  return buildSampleProject({
    title: s.title,
    text: s.text,
    codes: [
      { key: "barriers", name: s.barriers },
      { key: "cost", name: s.cost, parent: "barriers" },
      { key: "support", name: s.support },
      { key: "staff", name: s.staff, parent: "support" },
      { key: "peers", name: s.peers, parent: "support" },
    ],
    passages: [
      { code: "cost", quote: s.quoteFare },
      { code: "cost", quote: s.quotePass },
      { code: "cost", quote: s.quoteLunch },
      { code: "staff", quote: s.quoteStaff },
      { code: "peers", quote: s.quotePeers },
    ],
  });
}

export default function QualitativeCodingToolWidget() {
  const strings = useToolUi(QUALITATIVE_CODING_TOOL_STRINGS);
  const fmt = useToolNumber();
  const [project, setProject] = useState<Project>(emptyProject);
  const [hydrated, setHydrated] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [docId, setDocId] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [autoApply, setAutoApply] = useState(true);
  const [pending, setPending] = useState<{ start: number; end: number } | null>(
    null
  );
  const [draftTitle, setDraftTitle] = useState("");
  const [draftText, setDraftText] = useState("");
  const [filter, setFilter] = useState("");
  const [includeChildren, setIncludeChildren] = useState(true);
  const [importFailed, setImportFailed] = useState(false);
  const [confirm, setConfirm] = useState<"document" | "all" | null>(null);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const saved = raw ? parseProject(raw) : null;
      if (saved) {
        setProject(saved);
        setDocId(saved.documents[0]?.id ?? "");
        setActiveId(saved.codes[0]?.id ?? null);
      }
    } catch {
      // Storage can be blocked; the tool still works for this visit.
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) {
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, serializeProject(project));
      setSaveFailed(false);
    } catch {
      setSaveFailed(true);
    }
  }, [project, hydrated]);

  const doc =
    project.documents.find((item) => item.id === docId) ?? project.documents[0];
  const counts = useMemo(() => codeCounts(project), [project]);
  const covered = useMemo(() => coverage(project), [project]);
  const rows = useMemo(
    () => excerptsFor(project, filter === "" ? null : filter, includeChildren),
    [project, filter, includeChildren]
  );
  const activeCode = project.codes.find((code) => code.id === activeId);

  function applyRange(range: { start: number; end: number }) {
    if (!(doc && activeCode)) {
      setPending(range);
      return;
    }
    setProject((current) =>
      addSegment(current, doc.id, range.start, range.end, activeCode.id)
    );
    setPending(null);
    window.getSelection()?.removeAllRanges();
  }

  function handleSelection(range: { start: number; end: number } | null) {
    if (range && autoApply) {
      applyRange(range);
      return;
    }
    setPending(range);
  }

  function handleAddCode(name: string, parentId: string | null): boolean {
    const result = addCode(project, name, parentId);
    if (!result.id) {
      return false;
    }
    setProject(result.project);
    setActiveId(result.id);
    return true;
  }

  function handleAddDocument() {
    if (draftText.trim() === "") {
      return;
    }
    const title =
      draftTitle.trim() ||
      t(strings.untitled, { number: fmt(project.documents.length + 1) });
    const added = addDocument(project, title, draftText);
    setProject(added.project);
    setDocId(added.id);
    setDraftTitle("");
    setDraftText("");
  }

  function handleSample() {
    const sample = sampleProject(strings);
    setProject(sample);
    setDocId(sample.documents[0]?.id ?? "");
    setActiveId(sample.codes[0]?.id ?? null);
    setFilter("");
  }

  function handleRemoveCode(id: string) {
    setProject((current) => removeCode(current, id));
    setActiveId((current) => (current === id ? null : current));
    setFilter((current) => (current === id ? "" : current));
  }

  async function handleImport(file: File | undefined) {
    if (!file) {
      return;
    }
    const imported = parseProject(await file.text());
    setImportFailed(imported === null);
    if (imported) {
      setProject(imported);
      setDocId(imported.documents[0]?.id ?? "");
      setActiveId(imported.codes[0]?.id ?? null);
    }
  }

  const percent =
    covered.total === 0 ? 0 : (covered.coded / covered.total) * PERCENT;

  return (
    <>
      <Notice>{strings.privacy}</Notice>
      {saveFailed ? <ErrorNotice>{strings.saveFailed}</ErrorNotice> : null}
      <section className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          {project.documents.length > 0 ? (
            <div className="min-w-[220px] flex-1">
              <SelectField
                id="qc-doc"
                label={strings.documentLabel}
                onChange={setDocId}
                options={project.documents.map((item) => ({
                  value: item.id,
                  label: item.title,
                }))}
                value={doc?.id ?? ""}
              />
            </div>
          ) : null}
          <button
            className={BUTTON_SECONDARY}
            onClick={handleSample}
            type="button"
          >
            {strings.trySample}
          </button>
          {doc ? (
            <button
              className={BUTTON_SECONDARY}
              onClick={() => {
                if (confirm === "document") {
                  setProject((current) => removeDocument(current, doc.id));
                  setDocId("");
                  setConfirm(null);
                } else {
                  setConfirm("document");
                }
              }}
              type="button"
            >
              {confirm === "document"
                ? strings.removeDocumentConfirm
                : strings.removeDocument}
            </button>
          ) : null}
        </div>
        <details
          className="rounded-lg border border-border bg-card p-3"
          open={project.documents.length === 0}
        >
          <summary className={`${LABEL} cursor-pointer`}>
            {strings.addDocumentHeading}
          </summary>
          <div className="mt-3 space-y-3">
            <TextField
              id="qc-title"
              label={strings.titleLabel}
              onChange={setDraftTitle}
              placeholder={strings.titlePlaceholder}
              value={draftTitle}
            />
            <TextAreaField
              id="qc-text"
              label={strings.textLabel}
              onChange={setDraftText}
              placeholder={strings.textPlaceholder}
              prose
              rows={6}
              value={draftText}
            />
            <button
              className={BUTTON_PRIMARY}
              disabled={draftText.trim() === ""}
              onClick={handleAddDocument}
              type="button"
            >
              {strings.addDocument}
            </button>
          </div>
        </details>
      </section>
      {doc ? (
        <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
          <div className="space-y-3">
            <h2 className={WIDGET_H2}>{strings.readingHeading}</h2>
            <p className={HINT}>{strings.readingHint}</p>
            <CodingDocumentPane
              canCode={activeCode !== undefined}
              doc={doc}
              onCodeLine={applyRange}
              onSelection={handleSelection}
              project={project}
              strings={{
                documentLabel: strings.readingLabel,
                codeParagraph: strings.codeParagraph,
                noCode: strings.noCodeYet,
              }}
            />
            <Checkbox
              checked={autoApply}
              label={strings.autoApply}
              onChange={setAutoApply}
            />
            {pending && !autoApply ? (
              <div
                aria-live="polite"
                className="space-y-2 rounded-lg border border-border bg-card p-3"
              >
                <p className={LABEL}>{strings.selectionLabel}</p>
                <p className="text-foreground text-sm">
                  {doc.text.slice(pending.start, pending.end)}
                </p>
                {activeCode ? (
                  <button
                    className={BUTTON_PRIMARY}
                    onClick={() => applyRange(pending)}
                    type="button"
                  >
                    {t(strings.applySelection, { code: activeCode.name })}
                  </button>
                ) : (
                  <p className={HINT}>{strings.chooseCodeFirst}</p>
                )}
              </div>
            ) : null}
          </div>
          <CodingCodebook
            activeId={activeId}
            counts={counts}
            onActive={setActiveId}
            onAdd={handleAddCode}
            onRemove={handleRemoveCode}
            onRename={(id, name) =>
              setProject((current) => renameCode(current, id, name))
            }
            project={project}
            strings={strings}
          />
        </div>
      ) : (
        <>
          <Notice>{strings.noDocument}</Notice>
          {project.codes.length > 0 ? (
            <CodingCodebook
              activeId={activeId}
              counts={counts}
              onActive={setActiveId}
              onAdd={handleAddCode}
              onRemove={handleRemoveCode}
              onRename={(id, name) =>
                setProject((current) => renameCode(current, id, name))
              }
              project={project}
              strings={strings}
            />
          ) : null}
        </>
      )}
      <StatGrid>
        <StatCard
          label={strings.statDocuments}
          value={fmt(project.documents.length)}
        />
        <StatCard label={strings.statCodes} value={fmt(project.codes.length)} />
        <StatCard
          emphasis
          label={strings.statPassages}
          value={fmt(project.segments.length)}
        />
        <StatCard
          label={strings.statCoverage}
          value={t(strings.percent, {
            value: fmt(percent, { maximumFractionDigits: 1 }),
          })}
        />
      </StatGrid>
      <CodingExcerpts
        filter={filter}
        includeChildren={includeChildren}
        onFilter={setFilter}
        onIncludeChildren={setIncludeChildren}
        onMemo={(id, memo) =>
          setProject((current) => setMemo(current, id, memo))
        }
        onRemove={(id) => setProject((current) => removeSegment(current, id))}
        project={project}
        rows={rows}
        strings={strings}
      />
      <CodingExport
        confirmClear={confirm === "all"}
        importFailed={importFailed}
        onClear={() => {
          if (confirm === "all") {
            setProject(emptyProject());
            setDocId("");
            setActiveId(null);
            setFilter("");
            setConfirm(null);
          } else {
            setConfirm("all");
          }
        }}
        onImport={handleImport}
        project={project}
        strings={strings}
      />
    </>
  );
}
